import { defineStore } from 'pinia'
import type { SiteProResponseInterface } from '~/server/api/site-pro/get-data.post'
import type { AdditionalServicesConfig } from '~/server/api/site-pro/get-additional-services-config.post'
import findFittingPackage from '~/utils/findFittingPackage'
import type { PickupPointInterface } from '~/types/checkoutTypes'

import type { AllPackageInterface, PackageInterface } from '~/types/shipzeePro/packagesTypes'
import type {
  CountryId,
  CourierPackingOptions,
  ForwardRequestPackingOption,
  ForwardRequestPaymentPageDataInterface,
  ForwardRequestPaymentProcessorInterface,
} from '~/types/shipzeePro/shipzeeProTypes'
import type { FrontendDeliveryAddressInterface } from '~/types/addressTypes'
import ForwardRequestTabsEnum from '~/enums/ForwardRequestTabsEnum'
import type {
  ForwardRequestContentCredentialsInterface,
  ForwardRequestContentInterface,
  ForwardRequestInvoicePayment,
} from '~/types/forwardRequest/forwardRequestStoreTypes'
import { PaymentFeeTypes } from '~/enums/PaymentFeeTypes'
import { ClientAddressTypesEnum } from '~/enums/ClientAddressTypesEnum'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'
import PaymentProcessorsEnum from '~/enums/PaymentProcessorsEnum'
import { useLanguageStore } from './language'
import { Routes } from '~/enums/Routes'
import { useIdentity } from './identity'

function getInitialState(): ForwardRequestContentInterface {
  return {
    frId: null,
    new_packages_count: 0,
    total_packages_count: 0,
    shipped_packages_count: 0,
    selected_tab: ForwardRequestTabsEnum.ReadyShipmentsTab,
    in_review_current_page: 1,
    in_review_items: [],
    ready_current_page: 1,
    ready_items: [],
    all_items_current_page: 1,
    all_items: [],
    additional_services_config: null,
    selected_shipping_method: null,
    selected_items: [],
    destinations: [],
    selected_address: null,
    selected_delivery_method: null,
    selected_parcel_shop: null,
    selected_packing_option: null,
    invoice_removal: false,
    price_tag_removal: false,
    ship_in_original_package: false,
    insurance: false,
    insurance_price: 0,
    has_declared_credentials: false,
    credentials: {
      first_name: '',
      last_name: '',
      passport: '',
      files: [],
    },
    payment: {
      balance: null,
      stripe_token: null,
      finalPrice: 0,
      payment_methods: [],
      selected_payment_method: null,
    },
    payments: [],
    balanceRemoved: false,
  }
}

export const useSiteProContentStore = defineStore('shipzeeProContent', {
  state: (): ForwardRequestContentInterface => {
    return getInitialState()
  },
  getters: {
    activeTab(state) {
      return state.selected_tab
    },
    inReviewItems(state) {
      return state.in_review_items
    },
    inReviewItemsCurrentPage(state) {
      return state.in_review_current_page
    },
    readyItems(state) {
      return state.ready_items
    },
    readyItemsCurrentPage(state) {
      return state.ready_current_page
    },
    allItems(state) {
      return state.all_items
    },
    allItemsCurrentPage(state) {
      return state.all_items_current_page
    },
    totalPackagesCount(state) {
      return state.total_packages_count
    },
    shippedPackagesCount(state) {
      return state.shipped_packages_count
    },
    newPackagesCount(state) {
      return state.new_packages_count
    },
    additionalServicesConfig(state): AdditionalServicesConfig | null {
      return state.additional_services_config
    },
    declaredMinAmount(state): number {
      return state.additional_services_config?.customs.declared_min_amount ?? 0
    },
    selectedShippingMethod(state) {
      return state.selected_shipping_method
    },
    selectedItems(state) {
      return state.selected_items
    },
    selectedDeliveryMethod(state) {
      return state.selected_delivery_method
    },
    selectedAddress(state) {
      return state.selected_address
    },
    getDestinations(state) {
      return state.destinations
    },
    selectedPackingOption(state) {
      return state.selected_packing_option
    },
    invoiceRemoval(state) {
      return state.invoice_removal
    },
    priceTagRemoval(state) {
      return state.price_tag_removal
    },
    shipInOriginalPackage(state) {
      return state.ship_in_original_package
    },
    getInsurance(state) {
      return state.insurance
    },
    insurancePrice(state) {
      return state.insurance_price
    },
    hasDeclaredCredentials(state) {
      return state.has_declared_credentials
    },
    getCredentials(state) {
      return state.credentials
    },
    selectedParcelShop(state) {
      return state.selected_parcel_shop
    },
    selectedPaymentMethod(state) {
      if (state.payment.selected_payment_method) {
        return state.payment.payment_methods.find((_, index) => index === state.payment.selected_payment_method?.index)
      }

      return null
    },
    paymentTotalPrice(state) {
      const paymentMethod =
        state.payment.payment_methods.find((_, index) => index === state.payment.selected_payment_method?.index) ?? null
      let total = state.payment.finalPrice

      if (paymentMethod) {
        if (paymentMethod && typeof paymentMethod.fee === 'number' && typeof paymentMethod.fee_type !== 'undefined') {
          if (Number(paymentMethod.fee_type) === PaymentFeeTypes.Percentage) {
            total += (Number(state.payment.finalPrice) * Number(paymentMethod.fee)) / 100
          } else {
            total += paymentMethod.fee
          }
        }
      }

      if (this.payments?.length) {
        const balanceApplied = this.payments?.find(
          (payment) => payment.payment_processor_id === PaymentProcessorsEnum.ShipzeeBalance
        )

        if (balanceApplied) {
          total += balanceApplied.amount
        }
      }

      return total
    },
    getSelectedCourier(state) {
      const countryId: CountryId = state.selected_address?.country_id ?? null
      const destinationCouriers =
        state.destinations?.find((destination) => destination.country_id === countryId)?.couriers ?? []

      return destinationCouriers.find((courier) => courier.id === state.selected_delivery_method)
    },
    getPackingOptions(state): ForwardRequestPackingOption[] {
      if (state.selected_address && state.selected_delivery_method !== null) {
        const countryId: CountryId = state.selected_address?.country_id ?? null
        const destinationCouriers =
          state.destinations?.find((destination) => destination.country_id === countryId)?.couriers ?? []

        const courier = destinationCouriers.find((courier) => courier.id === state.selected_delivery_method)

        if (courier) {
          return (courier.packing_options ?? []).map(
            (option: CourierPackingOptions): ForwardRequestPackingOption => ({
              value: String(option.id),
              label: option.forward_request_label,
              data: option,
            })
          )
        }
      }

      return []
    },
    getDeclaredValue(state): number {
      return state.selected_items.reduce(
        (totalValue: number, item: PackageInterface) =>
          totalValue +
          item.declarations.reduce(
            (totalItemValue: number, declaration: { price: number; quantity: number }) =>
              totalItemValue + declaration.price * declaration.quantity,
            0
          ),
        0
      )
    },
    getInsuranceFeePercentage(state): number {
      return state.additional_services_config?.insurance.percentage ?? 0
    },
  },
  actions: {
    async fetch() {
      const nuxtApp = useNuxtApp()

      const { fetchProData, fetchAdditionalServicesConfig, fetchDestinations } = useProContent()

      const response = await nuxtApp.runWithContext(async () => await fetchProData())

      if (response.status === false) {
        this.$reset()

        return
      }

      const additionalServicesConfig = await nuxtApp.runWithContext(async () => await fetchAdditionalServicesConfig())

      const validResponse = response as SiteProResponseInterface
      this.total_packages_count = validResponse.total_packages_count
      this.new_packages_count = validResponse.new_packages_count
      this.shipped_packages_count = validResponse.shipped_packages_count
      this.all_items = validResponse.all_forward_requests
      this.ready_items = validResponse.ready_to_ship_packages
      this.in_review_items = validResponse.in_review_packages
      this.additional_services_config = additionalServicesConfig

      this.destinations = await nuxtApp.runWithContext(async () => await fetchDestinations())
    },
    async fetchPaymentData(requestId: number, cb: (data: ForwardRequestPaymentPageDataInterface) => void) {
      const client = useIdentity()
      if (client.user) {
        await client.refresh()
      }

      const response = await $fetch<ForwardRequestPaymentPageDataInterface>('/api/site-pro/get-payment-data', {
        method: 'POST',
        body: {
          id: requestId,
          languageId: useLanguageStore().getLanguageId,
        },
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        ...fetchRetryParameters,
      })

      if (response.status === false) {
        return navigateTo(getLocalizedRoute(Routes.Account.SitePro))
      }

      this.setFrId(requestId)
      this.setPayments(response.payments)
      this.setPaymentMethods(response.payment_processors)
      this.setPaymentFinalPrice(response.total)

      cb(response)
    },
    setActiveTab(i: ForwardRequestTabsEnum) {
      this.selected_tab = i
    },
    setReadyItemsCurrentPage(page: number) {
      this.ready_current_page = page
    },
    setReadyItems(items: PackageInterface[]) {
      this.ready_items = items
    },
    prependInReviewItem(item: PackageInterface) {
      this.in_review_items = [item, ...this.in_review_items]
    },
    updateAllItem(item: AllPackageInterface) {
      const index = this.all_items.findIndex((element: AllPackageInterface) => element.id === item.id)
      this.all_items[index] = item
    },
    setInReviewItemsCurrentPage(page: number) {
      this.in_review_current_page = page
    },
    setAllItemsCurrentPage(page: number) {
      this.all_items_current_page = page
    },
    setSelectedShippingMethod(method: { method: number; price: number }) {
      this.selected_shipping_method = method
    },
    setSelectedItems(items: PackageInterface[]) {
      this.selected_items = items
    },
    setSelectedDeliveryMethod(deliveryMethod: number | null) {
      this.setSelectedPackingOption(null)
      this.selected_parcel_shop = null
      this.selected_delivery_method = deliveryMethod
    },
    setSelectedAddress(address?: FrontendDeliveryAddressInterface) {
      this.setSelectedDeliveryMethod(null)
      this.has_declared_credentials = false
      this.selected_address = address || null

      if (address?.type === ClientAddressTypesEnum.COMMERCIAL) {
        this.invoice_removal = false
        this.price_tag_removal = false
      }
    },
    getCouriers() {
      const countryId = this.selected_address?.country_id ?? null
      const destinationCouriers =
        this.destinations?.find((destination) => destination.country_id === countryId)?.couriers ?? []

      const couriers = []
      for (const destinationCourier of destinationCouriers) {
        const fittingPackage = findFittingPackage(destinationCourier, this.selected_items)
        if (fittingPackage) {
          destinationCourier.price = fittingPackage.fr_price
          couriers.push(destinationCourier)
        }
      }

      return couriers
    },
    addItemDeclaration(
      id: number,
      declaration: { category: string; categoryLabel: string; quantity: number; price: number; hs_code: string }
    ) {
      const shipment = this.ready_items.find((item) => item.id === id)

      if (shipment) {
        shipment.declarations.push(declaration)
      }
    },
    removeItemDeclaration(id: number, index: number) {
      const shipment = this.ready_items.find((item) => item.id === id)

      if (shipment) {
        shipment.declarations.splice(index, 1)
      }
    },
    setItemDeclared(id: number, value: boolean) {
      const shipment = this.ready_items.find((item) => item.id === id)

      if (shipment) {
        shipment.declared = value
      }
    },
    setSelectedPackingOption(id: string | null) {
      this.selected_packing_option = id
    },
    toggleInvoiceRemoval() {
      this.invoice_removal = !this.invoice_removal
    },
    togglePriceTagRemoval() {
      this.price_tag_removal = !this.price_tag_removal
    },
    toggleShipInOriginalPackage() {
      this.ship_in_original_package = !this.ship_in_original_package
    },
    toggleInsurance() {
      this.insurance = !this.insurance
    },
    setInsurancePrice(price: number) {
      this.insurance_price = price
    },
    setHasDeclaredCredentials(value: boolean) {
      this.has_declared_credentials = value
    },
    setCredentials(data: ForwardRequestContentCredentialsInterface) {
      this.credentials = data
    },
    setSelectedParcelShop(parcelShop: PickupPointInterface | null) {
      this.selected_parcel_shop = parcelShop
    },
    setPaymentBalance(balance: { id: number; amount: number } | null) {
      this.payment.balance = balance
      if (this.payment.finalPrice === balance?.amount) {
        this.setSelectedPaymentMethod(null)
      }

      if (!balance) {
        this.removeBalance()
      }
    },
    setSelectedPaymentMethod(method: { id: number; index: number; type: number } | null) {
      this.payment.selected_payment_method = method
    },
    setPaymentMethods(methods: ForwardRequestPaymentProcessorInterface[]) {
      this.payment.payment_methods = methods
    },
    setStripeToken(token: string | null) {
      this.payment.stripe_token = token
    },
    setPaymentFinalPrice(price: number) {
      this.payment.finalPrice = price
    },
    setFrId(id: number) {
      this.frId = id
    },
    setPayments(payments: ForwardRequestInvoicePayment[]) {
      const balanceApplied = payments?.find(
        (payment) => payment.payment_processor_id === PaymentProcessorsEnum.ShipzeeBalance
      )
      if (balanceApplied) {
        this.payment.balance = {
          amount: balanceApplied?.amount,
          id: balanceApplied?.id,
        }
      }

      this.payments = payments
    },

    async removeBalance() {
      if (this.frId && this.payments?.length && !this.balanceRemoved) {
        const res = await $fetch<{
          status: boolean
        }>('/api/site-pro/remove-balance', {
          method: 'POST',
          body: {
            id: this.frId,
          },
          ...fetchRetryParameters,
          retry: 2,
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })

        if (res.status) {
          this.balanceRemoved = true
          this.payment.balance = null
        }
      }
    },
    resetDeliveryState(): void {
      this.selected_shipping_method = null
      this.selected_address = null
      this.selected_delivery_method = null
      this.selected_parcel_shop = null
      this.selected_packing_option = null
      this.invoice_removal = false
      this.price_tag_removal = false
      this.ship_in_original_package = false
      this.insurance = false
      this.insurance_price = 0
      this.has_declared_credentials = false
      this.credentials = {
        first_name: '',
        last_name: '',
        passport: '',
        files: [],
      }
    },
  },
})
